import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { CatalogsListing } from '@src/components/Catalogs/CatalogsListing';
import { CatalogsEditable } from '@src/components/Catalogs/CatalogsEditable';
import PageHeader from '@src/components/PageHeader/PageHeader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CatalogsAdd } from '@src/components/Catalogs/CatalogsAdd';
import { clearUserSession, generateCancelToken } from '@src/helpers/ApiHelper';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import {
	setAllCatalogs,
	setAllCustomers,
	setAllMasterCatalogProducts,
	setAllMasterCatalogs,
	setAllProducts,
	setPageNumber,
	setSupplierCatalog,
} from '@src/store/reducers/catalogReducer';
import moment from 'moment';
import { Alert, AlertColor, Snackbar } from '@mui/material';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import { Filters, TimeoutHandler } from '@src/interfaces/type';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;
`;

type Props = {};

export type CustomAlertType = {
	message: string;
	severity: AlertColor;
	closeTime?: number;
};

export interface SelectedItems {
	id: string;
	price?: string;
	customerPrice: string;
}

export type Catalog = {
	id: number;
	name: string;
	fromDate: string;
	toDate: string;
	published: boolean;
	restaurantId?: string;
	customerName?: string;
};

export type Product = {
	id: string;
	productId?: string;
	name: string;
	unit: string;
	price: string;
	category: string;
	categoryId: string;
	image?: string;
	images: any;
	description?: string;
	customerPrice?: string;
	edit?: boolean;
	customerPriceValue?: string;
	editBtnDisable?: boolean;
	disableRow?: boolean;
	selected: boolean;
};

export interface SelectedProduct {
	id: string;
	price: string;
	customerPrice?: string;
}

export const priceRegex = /^(\d*|\d+\.\d*|\.\d*)$/;

const Catalogs: React.FC<Props> = ({}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const { isDrawerOpen } = useAppSelector((state: any) => state?.UI);
	const isMobileWidth = IsMobileWidth();

	const pageContainerStyles: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		transition: 'width 0.3s ease',
		width:
			isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 240px)'
				: !isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 64px)'
				: '100%',
	};

	const allCatalogs: any = useAppSelector((state: any) => state?.supplierCatalog?.allCatalogs);
	const allProducts: any = useAppSelector((state: any) => state?.supplierCatalog?.allProducts);
	const allCustomers: any = useAppSelector((state: any) => state?.supplierCatalog?.allCustomers);

	const [isErrorVisible, setErrorVisible] = useState(false);
	const [alert, setAlert] = useState<CustomAlertType>({ message: '', severity: 'success' });
	const [errorMessage, setErrorMessage] = useState('');
	const [selectedRow, setSelectedRow] = useState();
	const [loadingData, setLoadingData] = useState(true);
	const [catalogs, setCatalogs] = useState<Catalog[]>([]);
	const [filteredCatalogs, setFilteredCatalogs] = useState<Catalog[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [customers, setCustomers] = useState([]);
	const [categoryId, setCategoryId] = useState('');
	const [searchValue, setSearchValue] = useState('');
	const [searching, setSearching] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
	const [productCount, setProductCount] = useState(0);
	const [loadingProducts, setLoadingProducts] = useState(false);
	const [tabValue, setTabValue] = useState('1');
	const [isApiCall, setApiCall] = useState(false);
	const [filters, setFilters] = useState<Filters | null>(null);

	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	const location = useLocation();
	const navigate = useNavigate();

	const { id, catalogId, masterCatalogId } = useParams();
	const cancelTokenSourceRef: any = useRef();
	const dispatch = useAppDispatch();
	const timeoutHandler = useRef<TimeoutHandler>(null);

	//Permission
	const checkPermission = usePermission();
	const canViewCatalogs = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.CATALOG?.VIEW, id);
	const canViewProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.VIEW, id);

	const handleRowClickListView = (params: any, event: any) => {
		const selectedCustomer: any = customers?.find(
			(customer: any) => customer?.userId === params?.row?.restaurantId
		);
		const row = { ...params?.row, customerName: selectedCustomer?.companyName };
		setSelectedRow(row);
		dispatch(setSupplierCatalog(row));
		navigate(`/suppliers/${id}/catalogs/edit/${params?.id}`);
	};

	const handleRowClickEditView = (params: any) => {
		console.log(params);
	};

	const displayCustomError = (error: AxiosError) => {
		setErrorMessage('Something went wrong');
		setErrorVisible(true);
	};

	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const activeBackIcon = () => {
		return (
			location?.pathname === `/suppliers/${id}/catalogs/edit/${catalogId}` ||
			location?.pathname === `/suppliers/${id}/catalogs/add` ||
			location?.pathname.startsWith(`/suppliers/${id}/catalogs/master`)
		);
	};

	const handleArrowBack = () => {
		navigate(-1);
	};

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
		}
	};

	const getCustomerPrice = (product: Product) => {
		if (!product?.customerPrice) {
			const selectedProduct = selectedProducts?.find(
				(item: any) => item?.id === product?.id?.toString()
			);
			return selectedProduct?.customerPrice || product?.price;
		}
		return product?.customerPrice || product?.price;
	};

	//TODO: Need to be update
	const getProducts = () => {
		apiService.getProducts(
			(response: AxiosResponse) => {
				const productsData = response?.data?.data;

				const data = productsData?.products?.map((product: any) => {
					const selectedState = selectedItemIds.includes(product?.id?.toString());
					return {
						...product,
						id: product?.id?.toString(),
						// unit: product?.attribute?.value,
						price: product?.price,
						categoryId: product?.category?.id,
						category: product?.category?.name,
						categoryObject: { id: product?.category?.id, value: product?.category?.name },
						customerPrice: getCustomerPrice(product),
						image: product?.image,
						editBtnDisable: !selectedState,
						selected: selectedState,
						subCategory: product?.subCategory?.name,
						subCategoryId: product?.subCategory?.id,
						machineBrand: product?.machineBrand?.name,
						machineBrandId: product?.machineBrand?.id,
						machineModel: product?.machineModel?.name,
						machineModelId: product?.machineModel?.id,
						groupCategory: product?.groupCategory?.name,
						groupCategoryId: product?.groupCategory?.id,
						brand: product?.brand?.name,
						brandId: product?.brand?.id,
						partComposition: product?.partComposition?.name,
						partCompositionId: product?.partComposition?.id,
						partDesign: product?.partDesign?.name,
						partDesignId: product?.partDesign?.id,
						partDiameter: product?.partDiameter?.name,
						partDiameterId: product?.partDiameter?.id,
						partDimension: product?.partDimension?.name,
						partDimensionId: product?.partDimension?.id,
						partOwnership: product?.partOwnership?.name,
						partOwnershipId: product?.partOwnership?.id,
						partQuality: product?.partQuality?.name,
						partQualityId: product?.partQuality?.id,
						partWeight: product?.partWeight?.name,
						partWeightId: product?.partWeight?.id,
						partPerformance: product?.partPerformance?.name,
						partPerformanceId: product?.partPerformance?.id,
					};
				});
				const totalCount = productsData?.totalCount;
				const totalPages = Math.max(Math.ceil(totalCount / 25), 1);

				if (page === 1) {
					setProducts(data);
				} else {
					setProducts((prevState: any) => [...prevState, ...data]);
				}


				setProductCount(totalCount);
				setTotalPages(totalPages);
				// dispatch(setAllProducts(data));
				setSearching(false);
				setSearching(false);
				setLoadingProducts(false);
			},
			(error: AxiosError) => {
				displayCustomError(error);
				setSearching(false);
			},
			{
				page: page,
				limit: 25,
				category: categoryId,
				search: searchValue,
				machineModel: filters?.model?.id,
				subCategory: filters?.subCategory?.id,
				machineBrand: filters?.machineBrand?.id,
				dimension: filters?.dimension?.id,
				design: filters?.design?.id,
				weight: filters?.weight?.id,
				composition: filters?.composition?.id,
				brand: filters?.brand?.id,
			}
		);
	};

	const getCustomers = (catalogsData: Catalog[]) => {
		apiService.getSupplierCustomers(
			(response: AxiosResponse) => {
				const customersData = response?.data?.data;
				if (canViewCatalogs) {
					let updatedCatalogsData: Catalog[] = [];
					catalogsData?.forEach((catalog: any) => {
						customersData?.forEach((customer: any) => {
							if (customer?.userId?.toString() === catalog?.restaurantId?.toString()) {
								updatedCatalogsData.push({ ...catalog, customerName: customer?.companyName });
							}
						});
					});
					// dispatch(setAllCatalogs(updatedCatalogsData));
					setCatalogs(updatedCatalogsData);
					setFilteredCatalogs(updatedCatalogsData);
				}

				const updatedCustomers = customersData?.map((customer: any) => {
					return {
						...customer,
						value: customer?.companyName,
						id: customer?.userId,
						// disable: customer?.isCatalogCreated,
					};
				});

				// dispatch(setAllCustomers(updatedCustomers));
				setCustomers(updatedCustomers);
				setLoadingData(false);
			},
			(error: AxiosError) => {
				displayCustomError(error);
				setLoadingData(false);
			}
		);
	};

	// useEffect(() => {
	// 	//persisted data
	// 	setCatalogs(allCatalogs);
	// 	setFilteredCatalogs(allCatalogs);
	// 	setProducts(allProducts);
	// 	setCustomers(allCustomers);

	// 	if (allProducts?.length && allCatalogs?.length && allCustomers?.length) {
	// 		setLoadingData(false);
	// 	}
	// }, [allCatalogs, allProducts, allCustomers]);

	useEffect(() => {
		if (canViewCatalogs) {
			cancelTokenSourceRef.current = generateCancelToken();

			apiService.getCatalogs(
				cancelTokenSourceRef.current.token,
				(response: AxiosResponse) => {
					const catalogsData: Catalog[] = response?.data?.data?.map((catalog: any) => ({
						...catalog,
						id: catalog?.catalogId,
						name: catalog?.name,
						fromDate: moment(catalog?.from).format('DD-MMMM-YYYY'),
						toDate: moment(catalog?.to).format('DD-MMMM-YYYY'),
						published: catalog?.status === 'Published',
						restaurantId: catalog?.restaurantId,
					}));

					//next call
					getCustomers(catalogsData);
					if (isApiCall) {
						setApiCall(false);
					}
				},
				(error: AxiosError) => {
					console.log(error);
					displayCustomError(error);
					setLoadingData(false);
				}
			);
		} else {
			getCustomers([]);
		}
	}, [isApiCall, canViewCatalogs]);

	useEffect(() => {
		if (canViewProduct) {
			getProducts();
		}
	}, [page, categoryId, filters]);

	//debounce
	useEffect(() => {
		if (canViewProduct) {
			//debounce
			if (timeoutHandler.current) {
				clearTimeout(timeoutHandler.current);
			}
			timeoutHandler.current = setTimeout(() => {
				try {
					getProducts();
				} catch (err) {}
			}, 500);
		} else {
		}
	}, [searchValue, canViewProduct]);

	const catalogRoutes = (path: string, id: string | undefined, catalogId: string | undefined) => {
		switch (path) {
			case `/suppliers/${id}/catalogs/add`:
				return (
					<CatalogsAdd
						catalogs={catalogs}
						setCatalogs={setCatalogs}
						filteredCatalogs={filteredCatalogs}
						setFilteredCatalogs={setFilteredCatalogs}
						customers={customers}
						setCustomers={setCustomers}
						products={products}
						setProducts={setProducts}
						loadingData={loadingData}
						displayCustomError={displayCustomError}
						alert={alert}
						setAlert={setAlert}
						setTabValue={setTabValue}
						setApiCall={setApiCall}
						totalPages={totalPages}
						page={page}
						setPage={setPage}
						setCategoryId={setCategoryId}
						handleChange={handlePageChange}
						searchValue={searchValue}
						setSearchValue={setSearchValue}
						searching={searching}
						setSearching={setSearching}
						selectedProducts={selectedProducts}
						setSelectedProducts={setSelectedProducts}
						selectedItemIds={selectedItemIds}
						setSelectedItemIds={setSelectedItemIds}
						productCount={productCount}
						filters={filters}
						setFilters={setFilters}
						loadingProducts={loadingProducts}
						setLoadingProducts={setLoadingProducts}
					/>
				);
			case `/suppliers/${id}/catalogs/edit/${catalogId}`:
				return (
					<CatalogsEditable
						customers={customers}
						catalogs={catalogs}
						setCatalogs={setCatalogs}
						filteredCatalogs={filteredCatalogs}
						setFilteredCatalogs={setFilteredCatalogs}
						selectedRow={selectedRow}
						setSelectedRow={setSelectedRow}
						displayCustomError={displayCustomError}
						alert={alert}
						setAlert={setAlert}
						setApiCall={setApiCall}
						filtersMC={filters}
						setFiltersMC={setFilters}
						// totalPages={totalPages}
						// page={page}
						// setPage={setPage}
						// setCategoryId={setCategoryId}
						// handleChange={handlePageChange}
						// searchValue={searchValue}
						// setSearchValue={setSearchValue}
						// searching={searching}
						// setSearching={setSearching}
						// selectedItemIds={selectedItemIds}
						// setSelectedItemIds={setSelectedItemIds}
					/>
				);
			default:
				// if (tabValue === '1') {
				// 	return (
				// 		<CatalogsListing
				// 			catalogs={masterCatalogs}
				// 			setCatalogs={setMasterCatalogs}
				// 			filteredCatalogs={filteredMasterCatalogs}
				// 			setFilteredCatalogs={setFilteredMasterCatalogs}
				// 			loadingData={loadingData}
				// 			handleRowClick={handleRowClickMasterCatalog}
				// 			tabValue={tabValue}
				// 			setTabValue={setTabValue}
				// 		/>
				// 	);
				// }
				return (
					<CatalogsListing
						catalogs={catalogs}
						setCatalogs={setCatalogs}
						filteredCatalogs={filteredCatalogs}
						setFilteredCatalogs={setFilteredCatalogs}
						loadingData={loadingData}
						handleRowClick={handleRowClickListView}
						tabValue={tabValue}
						setTabValue={setTabValue}
					/>
				);
		}
	};

	return (
		<StyledContainer style={pageContainerStyles}>
			<PageHeader
				title="Catalogs"
				activeIcon={activeBackIcon()}
				handleArrowBack={handleArrowBack}
			/>
			{catalogRoutes(location.pathname, id, catalogId)}

			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				open={isErrorVisible}
				autoHideDuration={6000}
				onClose={handleErrorClose}
			>
				<Alert onClose={handleErrorClose} severity="error" elevation={4}>
					{errorMessage}
				</Alert>
			</Snackbar>
		</StyledContainer>
	);
};

export default Catalogs;
